import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchProfile,
  type UserProfile,
} from '@/epics/userProfile/api/viewProfileApiService';
import { useNavigationHistory } from '@/shared/context/useNavigationHistory';
import { createContactRequest } from '../api/contactRequestApiService';

const SUBJECT_MAX = 200;
const DESCRIPTION_MAX = 500;
const DEFAULT_REDIRECT_PATH = '/Panel-główny';
const SUCCESS_MESSAGE =
  'Twoje zgłoszenie kontaktowe zostało pomyślnie wysłane.';
const INVALID_PREVIOUS_PATHS = new Set(['/login']);

interface FormErrors {
  subject?: string | undefined;
  description?: string | undefined;
}

export const useCreateContactRequest = () => {
  const navigate = useNavigate();
  const { stack } = useNavigationHistory();

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchProfile().then((result) => {
      if (cancelled) {
        return;
      }
      if (result.success && result.data) {
        setProfile(result.data);
      }
      setIsProfileLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!subject.trim()) {
      newErrors.subject = 'To pole jest wymagane';
    } else if (subject.length > SUBJECT_MAX) {
      newErrors.subject = `Temat może zawierać maksymalnie ${SUBJECT_MAX} znaków.`;
    }
    if (!description.trim()) {
      newErrors.description = 'To pole jest wymagane';
    } else if (description.length > DESCRIPTION_MAX) {
      newErrors.description = `Opis może zawierać maksymalnie ${DESCRIPTION_MAX} znaków.`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubjectChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const val = e.target.value;
    setSubject(val);
    if (errors.subject) {
      if (!val.trim()) {
        setErrors((prev) => ({
          ...prev,
          subject: 'Pole Temat jest wymagane.',
        }));
      } else if (val.length > SUBJECT_MAX) {
        setErrors((prev) => ({
          ...prev,
          subject: `Temat może zawierać maksymalnie ${SUBJECT_MAX} znaków.`,
        }));
      } else {
        setErrors((prev) => ({ ...prev, subject: undefined }));
      }
    }
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const val = e.target.value;
    setDescription(val);
    if (errors.description) {
      if (!val.trim()) {
        setErrors((prev) => ({
          ...prev,
          description: 'Pole Opis jest wymagane.',
        }));
      } else if (val.length > DESCRIPTION_MAX) {
        setErrors((prev) => ({
          ...prev,
          description: `Opis może zawierać maksymalnie ${DESCRIPTION_MAX} znaków.`,
        }));
      } else {
        setErrors((prev) => ({ ...prev, description: undefined }));
      }
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validate()) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    const result = await createContactRequest({
      subject: subject.trim(),
      description: description.trim(),
    });
    setIsSubmitting(false);
    if (result.success) {
      const previousPath = stack[stack.length - 2]?.pathname;
      const redirectPath =
        previousPath && !INVALID_PREVIOUS_PATHS.has(previousPath)
          ? previousPath
          : DEFAULT_REDIRECT_PATH;

      navigate(redirectPath, {
        replace: true,
        state: { navigationSuccessMessage: SUCCESS_MESSAGE },
      });
    } else {
      setSubmitError(result.error ?? 'Nie udało się utworzyć zgłoszenia.');
    }
  };

  const handleCancel = () => {
    const hasInput = subject.trim() || description.trim();
    if (hasInput) {
      setIsCancelModalOpen(true);
    } else {
      navigate(-1);
    }
  };

  const handleCloseCancelModal = () => setIsCancelModalOpen(false);

  const handleConfirmCancel = () => {
    setIsCancelModalOpen(false);
    navigate(-1);
  };

  const handleCloseErrorSnackbar = () => setSubmitError(null);

  return {
    subject,
    description,
    errors,
    isSubmitting,
    submitError,
    isCancelModalOpen,
    profile,
    isProfileLoading,
    handleSubjectChange,
    handleDescriptionChange,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseErrorSnackbar,
    SUBJECT_MAX,
    DESCRIPTION_MAX,
  };
};
